/* ============================================================
   Custom Cart Drawer
   Pairs with: sections/custom-cart-drawer.liquid

   - Opens from cart-icon clicks and (optionally) after add-to-cart.
   - Quantity / remove use /cart/change.js with the Section Rendering
     API ({ sections: 'custom-cart-drawer' }) so the drawer re-renders
     from the server (no manual money formatting).
   - Add-to-cart is OBSERVED (a light fetch wrapper) rather than
     intercepted, so the theme's own add logic keeps working.
   ============================================================ */
(function () {
  'use strict';

  var SECTION = 'custom-cart-drawer';

  function root() { return document.getElementById('CustomCartDrawer'); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function routeRoot() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }

  var lastFocus = null;

  // ── Open / close ─────────────────────────────────────
  function open() {
    var el = root();
    if (!el || el.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    var panel = el.querySelector('[data-ccd-panel]');
    var overlay = el.querySelector('[data-ccd-overlay]');
    if (panel) { panel.setAttribute('aria-hidden', 'false'); panel.inert = false; }
    if (overlay) overlay.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeydown);
    var closeBtn = el.querySelector('[data-ccd-close]');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 50);
  }

  function close() {
    var el = root();
    if (!el) return;
    el.classList.remove('is-open');
    var panel = el.querySelector('[data-ccd-panel]');
    if (panel) { panel.setAttribute('aria-hidden', 'true'); panel.inert = true; }
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
    lastFocus = null;
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'Tab') trapFocus(e);
  }

  function trapFocus(e) {
    var el = root();
    var panel = el && el.querySelector('[data-ccd-panel]');
    if (!panel) return;
    var focusables = $all(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
      panel
    ).filter(function (n) { return n.offsetParent !== null; });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ── Cart helpers ─────────────────────────────────────
  function setBusy(b) {
    var el = root();
    if (el) el.classList.toggle('is-busy', b);
  }

  function updateCartCount(count) {
    // Theme header cart bubble — best effort
    $all('.cart-bubble__text-count, .mm-mobile-nav__cart-count').forEach(function (n) {
      n.textContent = count;
      n.classList.toggle('hidden', count === 0);
    });
    $all('.cart-bubble').forEach(function (n) {
      n.classList.toggle('visually-hidden', count === 0);
    });
    document.dispatchEvent(new CustomEvent('ccd:cart-updated', { detail: { count: count } }));
  }

  function applySectionHTML(html) {
    var el = root();
    if (!el || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var freshContents = doc.querySelector('[data-ccd-contents]');
    var liveContents = el.querySelector('[data-ccd-contents]');
    if (freshContents && liveContents) {
      liveContents.innerHTML = freshContents.innerHTML;
    }
    var freshRoot = doc.querySelector('[data-ccd]');
    if (freshRoot) {
      var count = parseInt(freshRoot.getAttribute('data-cart-count'), 10) || 0;
      el.classList.toggle('ccd--empty', count === 0);
      updateCartCount(count);
    }
  }

  function changeQty(key, quantity) {
    if (!key) return;
    setBusy(true);
    fetch(routeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.sections && data.sections[SECTION]) {
          applySectionHTML(data.sections[SECTION]);
        } else {
          return refresh();
        }
      })
      .catch(function () { return refresh(); })
      .then(function () { setBusy(false); });
  }

  function refresh() {
    return fetch(routeRoot() + '?sections=' + SECTION, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) { applySectionHTML(data[SECTION]); })
      .catch(function () {});
  }

  // ── Add to cart (takes over the form so the drawer opens) ──
  function addToCart(form) {
    setBusy(true);
    var formData = new FormData(form);
    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (!res.ok || (res.body && res.body.status)) {
          // Out of stock / validation error — don't open an empty drawer
          console.warn('[CustomCartDrawer] add to cart failed:', (res.body && (res.body.description || res.body.message)) || res.body);
          setBusy(false);
          return;
        }
        refresh().then(function () { open(); setBusy(false); });
      })
      .catch(function () { setBusy(false); });
  }

  // Capture-phase: intercept add-to-cart submits before the theme / native
  // navigation so the custom drawer handles them.
  function interceptAddForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.nodeName !== 'FORM') return;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      addToCart(form);
    }, true);
  }

  // ── Click delegation ─────────────────────────────────
  function onClick(e) {
    var el = root();
    if (!el) return;

    // Open from cart-icon links (outside the drawer)
    var trigger = e.target.closest('.mm-header__cart, .mm-mobile-nav__cart, [data-ccd-toggle], a[href$="/cart"], a[href$="/cart/"]');
    if (trigger && !el.contains(trigger)) {
      e.preventDefault();
      open();
      return;
    }

    if (!el.contains(e.target)) return;

    if (e.target.closest('[data-ccd-close], [data-ccd-overlay]')) {
      e.preventDefault();
      close();
      return;
    }

    var qtyBtn = e.target.closest('[data-ccd-qty]');
    if (qtyBtn) {
      e.preventDefault();
      var item = qtyBtn.closest('[data-ccd-item]');
      var valEl = item && item.querySelector('.ccd__qty-value');
      var cur = valEl ? (parseInt(valEl.textContent, 10) || 1) : 1;
      var next = qtyBtn.getAttribute('data-ccd-qty') === 'up' ? cur + 1 : cur - 1;
      if (next < 0) next = 0;
      changeQty(qtyBtn.getAttribute('data-key'), next);
      return;
    }

    var removeBtn = e.target.closest('[data-ccd-remove]');
    if (removeBtn) {
      e.preventDefault();
      changeQty(removeBtn.getAttribute('data-key'), 0);
    }
  }

  // ── Open / refresh on the theme's cart events ────────
  // The theme dispatches a bubbling "cart:update" event after add-to-cart
  // (and other cart changes). We listen on document, re-render the drawer,
  // and open it after an add.
  function observeAddToCart() {
    document.addEventListener('cart:update', function () {
      var el = root();
      if (!el) return;
      // Ignore events caused by the drawer's own qty/remove (it sets is-busy)
      if (el.classList.contains('is-busy')) return;
      refresh().then(function () {
        if (el.getAttribute('data-open-after-add') === 'true') open();
      });
    });
  }

  function init() {
    var el = root();
    var panel = el && el.querySelector('[data-ccd-panel]');
    if (panel) panel.inert = true; // closed on load — keep out of tab order
    document.addEventListener('click', onClick);
    interceptAddForms();
    observeAddToCart();
    window.CustomCartDrawer = { open: open, close: close, refresh: refresh };
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
